/**
 * `<table-lobby>`: setting the table. Who sits where on each team, the link
 * that invites more people, and - for the host - the table size and the deal.
 *
 * It is a native modal <dialog> over the empty table. It stays up through a
 * game for anyone who came back through the link and still has to pick a seat
 * to drop into. Escape does not close it: getting up from a table is a choice
 * the Leave button makes, never a stray key press.
 */

import type { PlayerCount, TableLobbyMessage } from "../../server/protocol.js";
import { h, keepFocus } from "../dom.js";
import { emit } from "../events.js";
import { type LobbyTeam, canTakeSeat, getFreeColors, getOwnTeamId, groupTeams } from "../lobby.js";
import { TEAM_COLOR_LABELS } from "../palette.js";
import { BaseElement } from "./base-element.js";
import dialogStyles from "./dialogs.css";
import styles from "./table-lobby.css";

/** How long the copy button says the link was copied. */
const COPIED_MS = 2000;

/** How long a refusal from the server stays up. */
const NOTICE_MS = 5000;

export interface LobbyModel {
    readonly lobby: TableLobbyMessage;
    readonly inviteUrl: string;
}

const PLAYER_COUNT_LABELS: Readonly<Record<PlayerCount, string>> = {
    4: "4 players · 2 teams",
    6: "6 players · 3 teams"
};

export class TableLobby extends BaseElement {
    #model: LobbyModel | null = null;

    #copiedTimer: ReturnType<typeof setTimeout> | null = null;

    #noticeTimer: ReturnType<typeof setTimeout> | null = null;

    readonly #dialog = h("dialog", { "aria-labelledby": "lobby-heading" });

    readonly #heading = h("h2", { id: "lobby-heading" });

    readonly #intro = h("p", { class: "help" });

    readonly #notice = h("p", { class: "notice", role: "status", hidden: true });

    readonly #size = h("div", {});

    readonly #teams = h("div", { class: "teams" });

    readonly #standing = h("p", { class: "help", role: "status" });

    readonly #invite = h("input", { "type": "text", "readonly": true, "id": "invite-link", "aria-describedby": "invite-help" });

    readonly #copy = h("button", { type: "button" }, "Copy link");

    readonly #start = h("button", { type: "button", class: "primary" }, "Start game");

    readonly #waiting = h("p", { class: "help waiting" }, "Waiting for the host to start the game…");

    constructor() {
        super(dialogStyles, styles);

        const leave = h("button", { type: "button", class: "quiet" }, "Leave table");
        const body = h(
            "div",
            { class: "body" },
            this.#heading,
            this.#intro,
            this.#notice,
            this.#size,
            this.#teams,
            this.#standing,
            h(
                "div",
                { class: "field invite" },
                h("label", { class: "label", for: "invite-link" }, "Invite link"),
                h("div", { class: "invite-row" }, this.#invite, this.#copy),
                h("p", { class: "help", id: "invite-help" }, "Anyone with this link can come to the table and take a seat a bot is holding.")
            ),
            h("div", { class: "actions" }, leave, this.#waiting, this.#start)
        );

        leave.addEventListener("click", () => emit(this, "leave-table", null));
        this.#start.addEventListener("click", () => emit(this, "game-start", null));
        this.#copy.addEventListener("click", () => void this.#copyLink());
        this.#invite.addEventListener("focus", () => this.#invite.select());

        this.#dialog.addEventListener("cancel", (event) => event.preventDefault());

        this.#dialog.append(body);
        this.root.append(this.#dialog);
    }

    /** The table as the server last described it, or null to put the dialog away. */
    set model(value: LobbyModel | null) {
        this.#model = value;
        this.invalidate();
    }

    /**
     * Shows why the server refused a request - usually a seat someone else
     * took a moment sooner. It goes here rather than over the felt, which the
     * dialog's backdrop dims.
     */
    notify(text: string): void {
        this.#notice.textContent = text;
        this.#notice.hidden = false;

        if (this.#noticeTimer !== null) {
            clearTimeout(this.#noticeTimer);
        }

        this.#noticeTimer = setTimeout(() => {
            this.#noticeTimer = null;
            this.#notice.hidden = true;
        }, NOTICE_MS);
    }

    async #copyLink(): Promise<void> {
        try {
            await navigator.clipboard.writeText(this.#invite.value);
        }
        catch {
            // No clipboard outside a secure context: leave the link selected
            // for the player to copy themselves.
            this.#invite.focus();
            this.#invite.select();
            return;
        }

        this.#copy.textContent = "Copied";

        if (this.#copiedTimer !== null) {
            clearTimeout(this.#copiedTimer);
        }

        this.#copiedTimer = setTimeout(() => {
            this.#copiedTimer = null;
            this.#copy.textContent = "Copy link";
        }, COPIED_MS);
    }

    protected render(): void {
        const model = this.#model;

        if (model === null) {
            if (this.#dialog.open) {
                this.#dialog.close();
            }

            return;
        }

        const { lobby } = model;
        const seating = lobby.phase === "seating";

        this.#heading.textContent = seating ? "Set the table" : "Pick a seat";
        this.#intro.textContent = seating
            ? "Invite friends with the link below, or start now and bots will fill every open seat."
            : "This game is already under way. Take over any seat a bot is holding.";

        if (this.#invite.value !== model.inviteUrl) {
            this.#invite.value = model.inviteUrl;
        }

        keepFocus(this.root, () => {
            this.#renderSize(lobby);
            this.#teams.replaceChildren(...groupTeams(lobby).map((team) => this.#renderTeam(lobby, team)));
        });

        this.#standing.textContent = lobby.standing.length === 0
            ? ""
            : `Still choosing a seat: ${lobby.standing.join(", ")}`;
        this.#standing.hidden = lobby.standing.length === 0;

        this.#start.hidden = !seating || !lobby.isHost;
        this.#waiting.hidden = !seating || lobby.isHost;

        if (!this.#dialog.open) {
            this.#dialog.showModal();
        }
    }

    /** The host picks the size while seating; everyone else is just told it. */
    #renderSize(lobby: TableLobbyMessage): void {
        if (!lobby.isHost || lobby.phase !== "seating") {
            this.#size.replaceChildren(h(
                "div",
                { class: "field" },
                h("span", { class: "label" }, "Players"),
                h("p", {}, PLAYER_COUNT_LABELS[lobby.playerCount])
            ));
            return;
        }

        const choices = ([4, 6] as const).map((count) => {
            const input = h("input", {
                "type": "radio",
                "name": "players",
                "value": String(count),
                "checked": count === lobby.playerCount,
                "data-focus-key": `count-${count}`
            });

            input.addEventListener("change", () => {
                if (input.checked) {
                    emit(this, "player-count-change", { playerCount: count });
                }
            });

            return h("label", { class: "choice" }, input, h("span", {}, PLAYER_COUNT_LABELS[count]));
        });

        this.#size.replaceChildren(h(
            "fieldset",
            { class: "field" },
            h("legend", { class: "label" }, "Players"),
            h("div", { class: "choices" }, ...choices)
        ));
    }

    #renderTeam(lobby: TableLobbyMessage, team: LobbyTeam): HTMLElement {
        const ownTeam = getOwnTeamId(lobby) === team.teamId;
        const headingId = `team-${team.teamId}`;
        const section = h(
            "section",
            { "class": "team", "aria-labelledby": headingId },
            h(
                "h3",
                { id: headingId },
                h("span", { "class": "chip", "aria-hidden": "true" }),
                `${TEAM_COLOR_LABELS[team.color]} Team`,
                ownTeam ? h("span", { class: "tag" }, "Your team") : null
            ),
            ownTeam && lobby.phase === "seating" ? this.#renderColors(lobby, team) : null,
            h("ul", { class: "seats" }, ...team.seats.map((seat) => {
                const tags = [
                    seat.isYou ? "You" : null,
                    seat.isHost ? "Host" : null,
                    seat.isPlayer ? null : "Bot",
                    seat.away ? "Away" : null
                ].filter((tag) => tag !== null);

                const take = canTakeSeat(lobby, seat)
                    ? h("button", { "type": "button", "class": "take", "data-focus-key": `seat-${seat.seatIndex}` }, "Sit here")
                    : null;

                take?.setAttribute("aria-label", `Sit here, in place of ${seat.name}`);
                take?.addEventListener("click", () => emit(this, "seat-take", { seatIndex: seat.seatIndex }));

                return h(
                    "li",
                    { class: seat.isPlayer ? "seat player" : "seat bot" },
                    h("span", { class: "name" }, seat.name),
                    ...tags.map((tag) => h("span", { class: "tag" }, tag)),
                    take
                );
            }))
        );

        section.style.setProperty("--team", `var(--team-${team.color})`);

        return section;
    }

    /** Small swatches for the colors this team could switch to. */
    #renderColors(lobby: TableLobbyMessage, team: LobbyTeam): HTMLElement {
        const group = h("div", { "class": "colors", "role": "radiogroup", "aria-label": "Team color" });

        for (const color of getFreeColors(lobby, team.teamId)) {
            const input = h("input", {
                "type": "radio",
                "name": "team-color",
                "value": color,
                "class": "sr-only",
                "checked": color === team.color,
                "data-focus-key": `color-${color}`
            });
            const label = h(
                "label",
                { class: "dot", title: TEAM_COLOR_LABELS[color] },
                input,
                h("span", { class: "sr-only" }, TEAM_COLOR_LABELS[color])
            );

            label.style.setProperty("--swatch", `var(--team-${color})`);
            input.addEventListener("change", () => {
                if (input.checked) {
                    emit(this, "team-color-change", { color });
                }
            });

            group.append(label);
        }

        return group;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "table-lobby": TableLobby;
    }
}

customElements.define("table-lobby", TableLobby);
